import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { BookOpen, Check, ChevronDown, ChevronUp, Edit, MessageCircle, Mic, Phone, Plus, Send, X } from "lucide-react"
import { frageAendern, jetztSenden, versandText, type InhaberFrage, type InhaberFragenStand } from "@/lib/inhaberFragen"

/**
 * Rechte Spalte der Aufgaben-Seite (#271): Fragen an den Inhaber — neue
 * Antworten, wartende Fragen, Warteschlange für den nächsten Morgen.
 * „Aufgabe anlegen“ öffnet nur den Dialog; nichts entsteht automatisch (B9).
 */
export function InhaberFragenSpalte({
  stand, tenant, onNeueFrage, onBearbeiten, onAufgabeAnlegen,
}: {
  stand: InhaberFragenStand
  tenant: string
  /** Freie Frage ohne Aufgabe (Paul 03.11.2026) */
  onNeueFrage: () => void
  onBearbeiten: (f: InhaberFrage) => void
  onAufgabeAnlegen: (f: InhaberFrage) => void
}) {
  const qc = useQueryClient()
  const [meldung, setMeldung] = useState<string | null>(null)
  const aendern = useMutation({
    mutationFn: (a: { id: number; aenderung: Parameters<typeof frageAendern>[1] }) => frageAendern(a.id, a.aenderung),
    onSettled: () => qc.invalidateQueries({ queryKey: ["inhaber-fragen"] }),
  })
  // Paul 03.11.2026: jederzeit von Hand anstoßen, zusätzlich zu werktags 08:00
  const senden = useMutation({
    mutationFn: () => jetztSenden(tenant),
    onSuccess: (r) => setMeldung(`Gesendet: ${r.ergebnis}`),
    onError: (e) => setMeldung((e as Error).message),
    onSettled: () => qc.invalidateQueries({ queryKey: ["inhaber-fragen"] }),
  })

  const beantwortet = stand.items.filter((f) => f.status === "beantwortet")
  const wartet = stand.items.filter((f) => f.status === "gesendet")
  const frueher = stand.items.filter((f) => f.status === "erledigt")
    .sort((a, b) => (b.beantwortet_am || "").localeCompare(a.beantwortet_am || "")).slice(0, 10)
  const schlange = stand.items.filter((f) => f.status === "warteschlange").sort((a, b) => a.position - b.position || a.id - b.id)

  // Reihenfolge tauschen: beide Positionen neu setzen (fortlaufend, damit gleiche Werte nicht haengen bleiben)
  const tausche = (i: number, j: number) => {
    const neu = [...schlange];
    [neu[i], neu[j]] = [neu[j], neu[i]]
    neu.forEach((f, k) => { if (f.position !== k + 1) aendern.mutate({ id: f.id, aenderung: { position: k + 1 } }) })
  }

  return (
    <aside className="pp-card if-spalte" aria-label="Fragen an den Inhaber">
      <div className="if-kopf">
        <h3><MessageCircle className="w-4 h-4" />Fragen an den Inhaber</h3>
        <p>
          Automatisch <b>{versandText(stand.naechster_versand)}</b> per WhatsApp
          {stand.inhaber ? ` an ${stand.inhaber}` : ""} · höchstens {stand.max_pro_tag} neue am Tag
        </p>
        <div className="if-aktionen">
          <button className="pp-btn pp-btn-primary pp-btn-sm" onClick={onNeueFrage}>
            <Plus className="w-3.5 h-3.5" /> Frage an Inhaber stellen per WhatsApp
          </button>
          <button className="pp-btn pp-btn-sm" onClick={() => { setMeldung(null); senden.mutate() }}
            disabled={senden.isPending || !stand.aktiv || !stand.inhaber || (schlange.length === 0 && wartet.length === 0)}
            title="Warteschlange und offene Fragen sofort per WhatsApp schicken — nichts, was heute schon rausging">
            <Send className="w-3.5 h-3.5" /> {senden.isPending ? "Sendet …" : "Jetzt senden"}
          </button>
        </div>
        {meldung && <p className="if-meldung" role="status">{meldung}</p>}
        {!stand.aktiv && (
          <p className="if-aus">Für diesen Betrieb noch nicht eingeschaltet: Fragen werden gesammelt, aber nicht gesendet.</p>
        )}
        {stand.aktiv && !stand.inhaber && (
          <p className="if-aus">Kein Inhaber in den Team-Rufnummern („… (Inhaber)“) — es kann nichts gesendet werden.</p>
        )}
      </div>

      <section className="if-sek" aria-label="Neue Antworten">
        <h4>Neue Antworten{beantwortet.length > 0 && <span className="if-zahl ok">{beantwortet.length}</span>}</h4>
        {beantwortet.length === 0 ? <p className="if-leer">Noch keine Antworten.</p> : beantwortet.map((f) => (
          <div key={f.id} className="if-karte ok">
            <div className="if-vorgang">{f.vorgang}</div>
            <div className="if-frage">{f.frage}</div>
            <div className="if-antwort">
              {f.antwort_art === "sprache" ? <Mic className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
              „{f.antwort_text}“
            </div>
            <div className="if-zeile">
              <span><BookOpen className="w-3 h-3" />{f.logbuch_ok ? "Im HERO-Logbuch" : "Nicht im Logbuch (kein Vorgang)"}</span>
              <span className="if-platz" />
              <button className="pp-btn pp-btn-sm" onClick={() => onAufgabeAnlegen(f)}>Aufgabe anlegen</button>
              <button className="pp-btn pp-btn-sm" disabled={aendern.isPending}
                onClick={() => aendern.mutate({ id: f.id, aenderung: { status: "erledigt" } })}>Gesehen</button>
            </div>
          </div>
        ))}
      </section>

      <section className="if-sek" aria-label="Warten auf Antwort">
        <h4>Warten auf Antwort{wartet.length > 0 && <span className="if-zahl">{wartet.length}</span>}</h4>
        {wartet.length === 0 ? <p className="if-leer">Nichts offen.</p> : wartet.map((f) => (
          <div key={f.id} className="if-karte">
            <div className="if-vorgang">{f.vorgang}</div>
            <div className="if-frage">{f.frage}</div>
            <Knoepfe antworten={f.antworten} />
            <div className="if-zeile">
              {f.zustellung === "failed"
                ? <span className="if-anrufen">WhatsApp nicht zugestellt — „Jetzt senden“ schickt sie erneut</span>
                : f.lieber_anrufen
                ? <span className="if-anrufen"><Phone className="w-3 h-3" />{f.sendungen}× ohne Antwort, lieber anrufen</span>
                : <span>{f.angekuendigt ? "angekündigt, wartet auf seine Rückmeldung" : f.tage_offen ? `seit ${f.tage_offen} ${f.tage_offen === 1 ? "Tag" : "Tagen"}, geht wieder mit` : "heute gesendet"}</span>}
              <span className="if-platz" />
              <button className="ag-knopf" title="Zurückziehen" aria-label={`Zurückziehen: ${f.frage}`}
                onClick={() => aendern.mutate({ id: f.id, aenderung: { status: "zurueckgezogen" } })}><X className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </section>

      <section className="if-sek" aria-label="Geht als Nächstes raus">
        <h4>Geht als Nächstes raus{schlange.length > 0 && <span className="if-zahl">{schlange.length}</span>}</h4>
        {schlange.length === 0 ? <p className="if-leer">Noch keine Fragen. In der Liste unter einer Aufgabe auf „Aufgabe per WhatsApp an Inhaber fragen“ klicken — oder oben eine Frage ohne Aufgabe stellen.</p> : schlange.map((f, i) => (
          <div key={f.id} className="if-karte">
            <div className="if-zeile if-oben">
              <span className="if-nr">{i + 1}</span>
              <span className="if-vorgang">{f.vorgang}</span>
              <span className="if-platz" />
              <button className="ag-knopf" disabled={i === 0} aria-label="Nach oben" onClick={() => tausche(i, i - 1)}><ChevronUp className="w-3.5 h-3.5" /></button>
              <button className="ag-knopf" disabled={i === schlange.length - 1} aria-label="Nach unten" onClick={() => tausche(i, i + 1)}><ChevronDown className="w-3.5 h-3.5" /></button>
              <button className="ag-knopf" aria-label={`Bearbeiten: ${f.frage}`} onClick={() => onBearbeiten(f)}><Edit className="w-3.5 h-3.5" /></button>
              <button className="ag-knopf" aria-label={`Zurückziehen: ${f.frage}`}
                onClick={() => aendern.mutate({ id: f.id, aenderung: { status: "zurueckgezogen" } })}><X className="w-3.5 h-3.5" /></button>
            </div>
            <div className="if-frage">{f.frage}</div>
            <Knoepfe antworten={f.antworten} />
            {i >= stand.max_pro_tag && <p className="if-spaeter">Mehr als {stand.max_pro_tag} Fragen: geht einen Werktag später raus.</p>}
          </div>
        ))}
      </section>
      {frueher.length > 0 && (
        <details className="if-sek if-frueher">
          <summary>Frühere Antworten <span className="if-zahl">{frueher.length}</span></summary>
          {frueher.map((f) => (
            <div key={f.id} className="if-karte">
              {f.vorgang && <div className="if-vorgang">{f.vorgang}</div>}
              <div className="if-frage">{f.frage}</div>
              <div className="if-antwort leise">„{f.antwort_text}“</div>
            </div>
          ))}
        </details>
      )}
    </aside>
  )
}

function Knoepfe({ antworten }: { antworten: string[] }) {
  return (
    <div className="if-knoepfe">
      {antworten.length ? antworten.map((a) => <span key={a}>{a}</span>) : <span>freie Antwort</span>}
    </div>
  )
}
